/**
 * [PM-SG 2026-08-12] THE VICTORY SOUND.
 *
 * One asset, played when an announcement takes the screen. Everything here exists because a
 * browser will not simply play audio on request, and because this particular browser is on a wall
 * with nobody in front of it.
 *
 * THE AUTOPLAY RULE, AND WHY THIS IS NOT A BUG. Every modern browser refuses to start audio until
 * the page has received a real user gesture — a click, a tap, a key. A wall display never gets
 * one, and this board RELOADS ITSELF on every deploy, which resets the permission even if
 * somebody did tap it yesterday. So three things are done about it, in order of how well they
 * actually work:
 *
 *   1. THE KIOSK FLAG is the real fix, and it is not in this file: launch Chrome on the TV with
 *      --autoplay-policy=no-user-gesture-required. Then everything below just works, for ever.
 *   2. UNLOCK ON ANY GESTURE. The context is created lazily and resumed from a one-shot listener
 *      on pointerdown/keydown, so if a person ever touches the screen, sound works for the rest
 *      of that session.
 *   3. SAY SO. `wasBlocked()` reports that a play was attempted and refused, so the page can show
 *      a small "tap for sound" chip instead of being mysteriously silent.
 *
 * WHY WEB AUDIO AND NOT `new Audio()`. The file is decoded ONCE into a buffer, so the sound starts
 * on the frame it is asked for rather than after a fetch; the gain node lets it fade in instead of
 * clipping; and a suspended context is detectable, where an <audio> element's rejected play()
 * promise is easy to swallow by accident.
 */

/**
 * Served from client/public — react-scripts copies it into build/, so it survives every deploy.
 *
 * [2026-08-12] Applause, not a fanfare. The first asset here was a CC0 "victory fanfare" that
 * turned out to be a regal march — "it feels like any king is coming" — which is what happens
 * when you ship audio nobody has listened to. A crowd is unambiguous: it cannot be mistaken for
 * anything except people being pleased, which is the entire message of this page.
 *
 * Public domain (Wikimedia Commons, Applause_i.ogg), so there is nothing to attribute and
 * nothing to renew.
 *
 * ⚠ [PM-SG 2026-08-18] THE APPLAUSE IS NO LONGER PLAYED. At the user's request the cue is now the
 * alert followed by a beats bed and nothing else — "just beep unchanged and then the 5 second of
 * beats.mp3". victory.ogg is deliberately LEFT IN public/ rather than deleted: it is never
 * fetched, so it costs nothing on the wire, and it makes going back a one-line change right here.
 *
 * beats.mp3 is 20.43s, MPEG-1 Layer III, 44.1 kHz stereo, 128 kbps CBR. Licensed for commercial
 * use — confirmed by the user on 2026-08-18, who noted the board is shown inside the organisation
 * only. Recorded in public/CREDITS.md, which is the standing rule for every sound on this board.
 */
/**
 * ⚠ THE ?v= IS LOAD-BEARING. BUMP IT WHENEVER THE FILE CHANGES.
 *
 * The site sits behind Cloudflare, which caches this path for four hours (max-age=14400), and the
 * server answers an unknown path with the SPA's index.html rather than a 404. Those two facts
 * combine badly: one request for /beats.mp3 in the window before the deploy landed was enough for
 * Cloudflare to cache 899 bytes of HTML under this URL and serve it for the next four hours.
 *
 * That is not a quiet failure. decodeAudioData rejects on HTML, loadBuffer swallows it and returns
 * null, and playVictory then bails at "if (!buffer) return" — so the board loses THE WHOLE CUE,
 * the synthesised alert included, and does it silently. Verified live on 2026-08-18: the bare path
 * returned 899 bytes of text/html with cf-cache-status HIT while /beats.mp3?v=... returned the
 * real 326,843 bytes of audio/mpeg from the same box.
 *
 * A version token gives each new file its own URL, which no cache can have a stale entry for.
 * Swap the track without bumping this and the board plays the OLD audio for up to four hours.
 */
const SRC = '/beats.mp3?v=1';

const MUTE_KEY = 'pmsg-muted';

/** Where the bed comes in — under the tail of the release tone, so the count and the music
 *  overlap rather than queue. Three beeps at 0.26s apart plus the release puts that at ~0.95s.
 *  This is what "after the beep" means in practice: the alert still owns the first second alone. */
const BED_IN_S = 0.95;
/**
 * Five seconds of the twenty on the file — the user's figure, 2026-08-18.
 *
 * ⚠ THIS IS THE FULL-LEVEL PORTION, AND THE FADE COMES AFTER IT, NOT INSIDE IT. The user asked for
 * five seconds and then a smooth ending — "the volume for this will become low low low and then
 * stops" — so the ramp is TAIL_S on top of these five rather than carved out of them. Fold the
 * fade back inside and the sound is five seconds long but only about three of them are at level,
 * which is not what was asked for.
 */
const BED_S = 5.0;
/** WHICH five: 0 is the first five seconds, as asked. Raise it to skip an intro — the offset is
 *  read straight into src.start(), so choosing a different passage never means re-encoding. */
const BED_FROM_S = 0;
/**
 * ⚠ THE ONE NUMBER TO TURN IF IT IS TOO LOUD OR TOO QUIET. The applause this replaces sat at 0.62
 * under the same 0.85 master; this is higher because the bed now carries the cue by itself rather
 * than sitting under a crowd. Chosen on arithmetic — nobody has heard this file on the board yet.
 */
const BED_VOL = 0.7;
/**
 * THE SMOOTH ENDING, and it sits AFTER the five seconds rather than inside them.
 *
 * The applause this replaces used 2.1s: broadband noise has no melody to follow, so the ear hears
 * only the level and a short fade on a dense crowd is heard as a cut. A rhythmic track carries a
 * pulse the ear can already follow, so it needs less — but it still needs enough to read as an
 * ending rather than a chop mid-bar, and the user asked for it to go "low low low and then stop",
 * which is a decay with stages in it, not a straight line to zero.
 *
 * ⚠ IT IS NOT FREE. It extends the cue and the card is only up for 10s — see PLAY_S.
 */
const TAIL_S = 1.6;
/**
 * ⚠ THE ASSET IS LONGER THAN THE SOUND — 20.43s on file, 6.6 of them used (5 at level + 1.6 fade).
 *
 * Capping here rather than trimming the file is deliberate: any file dropped in to replace this
 * one is automatically held to the same window, so swapping the sound stays a one-file change
 * with no re-encoding.
 *
 * ⚠ THIS IS THE WHOLE CUE, NOT THE BED. The bed enters at BED_IN_S and then needs BED_S at level
 * plus TAIL_S to fade, so all three terms belong here. A flat 5.0 would yield only 4.05s of music
 * and no ending at all — the exact mistake this line exists to prevent.
 *
 * ⚠ COUPLED TO ANNOUNCE_MS IN PMSingaporePage, which the user raised to 15s on 2026-08-18 for
 * exactly this reason. At 7.55s the cue leaves ~7.4s of margin; a cue that outlasts the card is
 * heard as the board talking over itself. Lengthen BED_S or TAIL_S and check that number too —
 * it carries the same warning pointing back here.
 */
const PLAY_S = BED_IN_S + BED_S + TAIL_S;

let ctx: AudioContext | null = null;
let buffer: AudioBuffer | null = null;
let loading: Promise<void> | null = null;
let blocked = false;
let armed = false;

function audioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try { ctx = new Ctor(); } catch { return null; }
  }
  return ctx;
}

/** Default ON: the sound was asked for. Muting is a deliberate act and it has to survive the
 *  deploy-reload, so it lives in localStorage rather than in React state. */
export function isMuted(): boolean {
  try { return window.localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

export function setMuted(v: boolean): void {
  try { window.localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch { /* private mode */ }
  if (!v) { blocked = false; void unlock(); }
}

/** True when a play was attempted and the browser refused for want of a gesture. */
export function wasBlocked(): boolean {
  return blocked;
}

async function unlock(): Promise<void> {
  const c = audioCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    try { await c.resume(); } catch { /* still needs a gesture */ }
  }
  if (c.state === 'running') blocked = false;
}

/**
 * Listen ONCE for any gesture and resume the context. Cheap enough to arm on mount and it is the
 * only thing standing between a silent board and a working one when the kiosk flag is missing.
 */
export function armAudio(): () => void {
  if (armed) return () => {};
  armed = true;
  const on = () => { void unlock(); };
  const opts = { once: true, passive: true } as AddEventListenerOptions;
  window.addEventListener('pointerdown', on, opts);
  window.addEventListener('keydown', on, opts);
  void preload();
  return () => {
    window.removeEventListener('pointerdown', on);
    window.removeEventListener('keydown', on);
    armed = false;
  };
}

/** Fetch + decode once, well before the first announcement needs it. */
export function preload(): Promise<void> {
  if (loading) return loading;
  loading = (async () => {
    const c = audioCtx();
    if (!c) return;
    try {
      const res = await fetch(SRC, { cache: 'force-cache' });
      if (!res.ok) return;
      buffer = await c.decodeAudioData(await res.arrayBuffer());
    } catch {
      /* a missing or unreadable asset must never break the board — it just stays quiet */
      buffer = null;
    }
  })();
  return loading;
}

/**
 * THE ALERT that opens the sound — the "siren", built the way Formula 1 would actually build it.
 *
 * ⚠ THERE IS NO SIREN IN F1, and that is not pedantry, it is why this sounds expensive instead of
 * cheap. A Grand Prix start is five lights and then SILENCE; the drama is the absence of sound.
 * What the sport does have, and what everyone recognises, is the TEAM RADIO BEEP and race
 * control's message alert: a short, clean two-tone that means "listen, something has been
 * decided". A wailing siren on an office wall reads as an emergency and, worse, as a free sound
 * effect. This is the broadcast version of the same instruction.
 *
 * Three parts, all synthesised so there is no second asset to ship or 404:
 *   a low thump    weight, so the alert has a body rather than being a bare bleep
 *   two tones      G5 then D6 — a rising fifth, which reads as "attention", where a falling
 *                  interval reads as "problem"
 *   a soft tail    the second tone rings a little longer, so the bed fades in UNDER it rather
 *                  than starting after a gap
 */
function alertTone(c: AudioContext, t0: number, out: AudioNode): void {
  /**
   * A COUNT, not a single ping. Three even beeps on one pitch, then a fourth a fifth higher that
   * releases into the bed. Evenly spaced repetition is what the ear reads as a countdown —
   * it says "something is about to be said" and gives the room the half-second it needs to look
   * up, which a single tone does not. It is also the closest honest analogue to a Grand Prix
   * start, where the tension is in the counting and the release is the moment it stops.
   */
  const BEEP = { f: 784, len: 0.1, vol: 0.3 };   // G5
  const STEP = 0.26;
  for (let i = 0; i < 3; i++) {
    const at = t0 + i * STEP;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = 'triangle';                        // carries across a room; no sawtooth buzz
    osc.frequency.setValueAtTime(BEEP.f, at);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(BEEP.vol, at + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, at + BEEP.len);
    osc.connect(g).connect(out);
    osc.start(at);
    osc.stop(at + BEEP.len + 0.04);
  }

  // The release, a fifth up and held longer, so the bed can come in underneath its tail.
  const rel = t0 + 3 * STEP;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(1175, rel);        // D6
  g.gain.setValueAtTime(0.0001, rel);
  g.gain.exponentialRampToValueAtTime(0.32, rel + 0.014);
  g.gain.exponentialRampToValueAtTime(0.0001, rel + 0.5);
  osc.connect(g).connect(out);
  osc.start(rel);
  osc.stop(rel + 0.55);

  // Weight under the release only: a short sub drop, felt more than heard. Under every beep it
  // would be a drum pattern; under the release it is the thing that makes it land.
  const sub = c.createOscillator();
  const subGain = c.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(150, rel);
  sub.frequency.exponentialRampToValueAtTime(58, rel + 0.3);
  subGain.gain.setValueAtTime(0.0001, rel);
  subGain.gain.exponentialRampToValueAtTime(0.4, rel + 0.02);
  subGain.gain.exponentialRampToValueAtTime(0.0001, rel + 0.45);
  sub.connect(subGain).connect(out);
  sub.start(rel);
  sub.stop(rel + 0.5);
}


/**
 * Play it. Safe to call at any time: muted, unloaded, unsupported and blocked all return quietly.
 * Never throws — this is decoration on a board whose figures matter.
 */
export async function playVictory(): Promise<void> {
  if (isMuted()) return;
  const c = audioCtx();
  if (!c) return;

  await preload();
  if (!buffer) return;

  if (c.state === 'suspended') {
    try { await c.resume(); } catch { /* ignored */ }
  }
  if (c.state !== 'running') { blocked = true; return; }
  blocked = false;

  try {
    const t = c.currentTime;

    /* One master gain for the whole cue, so the alert and the bed are mixed against each other
       once rather than each being tuned against the room separately. */
    const master = c.createGain();
    master.gain.setValueAtTime(0.85, t);
    master.connect(c.destination);

    // 1. the alert
    alertTone(c, t, master);

    // 2. the beats bed, coming in under its tail
    const src = c.createBufferSource();
    src.buffer = buffer;
    const gain = c.createGain();
    const start = t + BED_IN_S;
    const end = t + PLAY_S;
    /* ⚠ THE EXCERPT IS THE FIVE SECONDS PLUS THE TAIL, NOT FIVE IN TOTAL. Audio has to keep
       playing THROUGH the fade — cut the source at five and the "smooth ending" would be a fade
       applied to silence. Clamped to the file so a shorter replacement plays all it has instead
       of running the source dry. */
    const len = Math.max(0, Math.min(BED_S + TAIL_S, buffer.duration - BED_FROM_S));

    /* Full level for the whole five seconds. Everything after this instant is the ending. */
    const holdTo = start + BED_S;
    gain.gain.setValueAtTime(0.0001, start);
    /* A rise rather than a cut, for the same reason as the applause: a bed that starts at full
       level sounds like a sample being triggered. Quicker than the crowd's swell was, because a
       beat has an attack of its own and a slow fade-in would blur the first bar. */
    gain.gain.exponentialRampToValueAtTime(BED_VOL, start + 0.22);
    gain.gain.setValueAtTime(BED_VOL, holdTo);
    /* "Low, low, low, and then it stops" — a STAGED decay rather than one straight run to zero.
       Three exponential steps each read as an audible drop in level, so the ear hears the sound
       being taken away deliberately instead of a single smear; the closing linear run to true
       silence removes the step that an exponential ramp always leaves behind at the bottom. */
    gain.gain.exponentialRampToValueAtTime(BED_VOL * 0.45, holdTo + TAIL_S * 0.34);
    gain.gain.exponentialRampToValueAtTime(BED_VOL * 0.18, holdTo + TAIL_S * 0.66);
    gain.gain.exponentialRampToValueAtTime(0.04, end - 0.12);
    gain.gain.linearRampToValueAtTime(0.0001, end);

    /* The MASTER guards the very end only. It used to ride down alongside the applause, but the
       bed now owns its own staged decay and a second overlapping fade would multiply into
       something far steeper than "low, low, low". This is left as pure anti-click insurance:
       the alert's ring-out and any rounding in the buffer's tail must not be audible at the
       moment the source is stopped. */
    master.gain.setValueAtTime(0.85, end - 0.1);
    master.gain.linearRampToValueAtTime(0.0001, end + 0.02);

    src.connect(gain).connect(master);
    /* Three-argument start: WHEN to play, WHERE in the file to begin, and HOW MUCH to take. The
       offset is what makes "which five seconds" a constant instead of a re-encode. */
    src.start(start, BED_FROM_S, len);
    /* Stop AFTER the fade has finished, never on top of it. */
    src.stop(Math.min(start + len, end) + 0.08);
  } catch { /* nothing worth breaking the page for */ }
}
